const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export type DateGroup = "Today" | "Yesterday" | "This week" | "Earlier";

/** Midnight of the given date, in the viewer's own timezone. */
function startOfDay(date: Date): number {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy.getTime();
}

/** "3h ago", "2d ago" — falls back to an absolute date beyond a month. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return "";

  const delta = now - time;
  if (delta < 0) return "just now";
  if (delta < MINUTE) return "just now";
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m ago`;
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h ago`;
  if (delta < 30 * DAY) return `${Math.floor(delta / DAY)}d ago`;

  return new Date(time).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Unambiguous absolute timestamp, used for tooltips and <time dateTime>. */
export function absoluteTime(iso: string): string {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return "";
  return new Date(time).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function dateGroupOf(iso: string, now: number = Date.now()): DateGroup {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return "Earlier";

  const today = startOfDay(new Date(now));
  if (time >= today) return "Today";
  if (time >= today - DAY) return "Yesterday";
  if (time >= today - 7 * DAY) return "This week";
  return "Earlier";
}
